import { supabase } from './client';

// Constante para a chave no localStorage
const OPENAI_PROMPTS_CACHE_KEY = 'voah_openai_prompts';

export interface OpenAIPrompt {
  id: string;
  name: string;
  description?: string;
  promptText: string;
  category?: string;
  isDefault: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface SupabaseOpenAIPrompt {
  id: string;
  name: string;
  description?: string;
  prompt_text: string;
  category?: string;
  is_default: boolean;
  created_at?: string;
  updated_at?: string;
}

// Converter do formato Supabase para o formato da aplicação
const fromSupabasePrompt = (prompt: SupabaseOpenAIPrompt): OpenAIPrompt => {
  return {
    id: prompt.id,
    name: prompt.name,
    description: prompt.description || '',
    promptText: prompt.prompt_text,
    category: prompt.category || '',
    isDefault: prompt.is_default,
    createdAt: prompt.created_at ? new Date(prompt.created_at) : new Date(),
    updatedAt: prompt.updated_at ? new Date(prompt.updated_at) : new Date()
  };
};

// Converter do formato da aplicação para o formato Supabase
const toSupabasePrompt = (prompt: Partial<OpenAIPrompt>): Partial<SupabaseOpenAIPrompt> => {
  const result: Partial<SupabaseOpenAIPrompt> = {};
  
  if (prompt.name !== undefined) result.name = prompt.name;
  if (prompt.description !== undefined) result.description = prompt.description;
  if (prompt.promptText !== undefined) result.prompt_text = prompt.promptText;
  if (prompt.category !== undefined) result.category = prompt.category;
  if (prompt.isDefault !== undefined) result.is_default = prompt.isDefault;
  
  return result;
};

// Salvar prompts no localStorage
const savePromptsToLocalStorage = (prompts: OpenAIPrompt[]): void => {
  try {
    localStorage.setItem(OPENAI_PROMPTS_CACHE_KEY, JSON.stringify(prompts));
  } catch (error) {
    console.error('Erro ao salvar prompts no cache:', error);
  }
};

// Carregar prompts do localStorage
const getPromptsFromLocalStorage = (): OpenAIPrompt[] => {
  try {
    const data = localStorage.getItem(OPENAI_PROMPTS_CACHE_KEY);
    if (!data) return [];
    return JSON.parse(data);
  } catch (error) {
    console.error('Erro ao carregar prompts do cache:', error);
    return [];
  }
};

// Limpar cache de prompts
const clearPromptsCache = (): void => {
  try {
    localStorage.removeItem(OPENAI_PROMPTS_CACHE_KEY);
  } catch (error) {
    console.error('Erro ao limpar cache de prompts:', error);
  }
};

export const promptsService = {
  // Obter todos os prompts
  getPrompts: async (useCache = true): Promise<OpenAIPrompt[]> => {
    // Verificar se temos dados em cache
    if (useCache) {
      const cachedPrompts = getPromptsFromLocalStorage();
      if (cachedPrompts.length > 0) {
        return cachedPrompts;
      }
    }

    // Buscar do Supabase
    try {
      const { data, error } = await supabase
        .from('openai_prompts')
        .select('*')
        .order('name', { ascending: true });
      
      if (error) {
        throw error;
      }
      
      // Converter para o formato da aplicação
      const prompts = (data || []).map(fromSupabasePrompt);
      
      // Atualizar o cache
      savePromptsToLocalStorage(prompts);
      
      return prompts;
    } catch (error) {
      // Se falhar, tentar cache como última opção
      const cachedPrompts = getPromptsFromLocalStorage();
      return cachedPrompts;
    }
  },

  // Obter um prompt específico pelo ID
  getPromptById: async (id: string, useCache = true): Promise<OpenAIPrompt | null> => {
    // Verificar se temos o prompt em cache
    if (useCache) {
      const cachedPrompts = getPromptsFromLocalStorage();
      const cachedPrompt = cachedPrompts.find(p => p.id === id);
      if (cachedPrompt) {
        return cachedPrompt;
      }
    }

    // Buscar do Supabase
    try {
      const { data, error } = await supabase
        .from('openai_prompts')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        return null;
      }
      
      return data ? fromSupabasePrompt(data) : null;
    } catch (error) {
      return null;
    }
  },

  // Obter um prompt pelo nome
  getPromptByName: async (name: string, useCache = true): Promise<OpenAIPrompt | null> => {
    // Verificar se temos o prompt em cache
    if (useCache) {
      const cachedPrompts = getPromptsFromLocalStorage();
      const cachedPrompt = cachedPrompts.find(p => p.name === name);
      if (cachedPrompt) {
        return cachedPrompt;
      }
    }

    // Buscar do Supabase
    try {
      const { data, error } = await supabase
        .from('openai_prompts')
        .select('*')
        .eq('name', name)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') {
        // PGRST116 é o código para "nenhum resultado encontrado" com maybeSingle()
        return null;
      }
      
      if (!data) {
        return null;
      }
      
      const prompt = fromSupabasePrompt(data);
      
      // Atualizar o cache com este prompt
      const cachedPrompts = getPromptsFromLocalStorage();
      const updatedCache = cachedPrompts.filter(p => p.name !== name);
      updatedCache.push(prompt);
      savePromptsToLocalStorage(updatedCache);
      
      return prompt;
    } catch (error) {
      return null;
    }
  },

  // Criar um novo prompt
  createPrompt: async (prompt: Omit<OpenAIPrompt, 'id' | 'createdAt' | 'updatedAt'>): Promise<OpenAIPrompt> => {
    try {
      // Verificar se já existe um prompt com este nome
      const existingPrompt = await promptsService.getPromptByName(prompt.name, true);
      if (existingPrompt) {
        return promptsService.updatePrompt(existingPrompt.id, prompt);
      }
      
      const supabasePrompt = toSupabasePrompt(prompt);
      
      const { data, error } = await supabase
        .from('openai_prompts')
        .insert([supabasePrompt])
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      // Converter para o formato da aplicação
      const newPrompt = fromSupabasePrompt(data);
      
      // Atualizar o cache (buscar todos novamente para garantir consistência)
      clearPromptsCache();
      
      return newPrompt;
    } catch (error) {
      throw error;
    }
  },

  // Atualizar um prompt existente
  updatePrompt: async (id: string, updates: Partial<Omit<OpenAIPrompt, 'id' | 'createdAt' | 'updatedAt'>>): Promise<OpenAIPrompt> => {
    try {
      const supabaseUpdates = toSupabasePrompt(updates);
      
      // Adicionar timestamp de atualização
      supabaseUpdates.updated_at = new Date().toISOString();
      
      const { data, error } = await supabase
        .from('openai_prompts')
        .update(supabaseUpdates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      // Converter para o formato da aplicação
      const updatedPrompt = fromSupabasePrompt(data);
      
      // Atualizar o cache (limpar para forçar recarga dos dados)
      clearPromptsCache();
      
      return updatedPrompt;
    } catch (error) {
      throw error;
    }
  },

  // Excluir um prompt
  deletePrompt: async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('openai_prompts')
        .delete()
        .eq('id', id);
      
      if (error) {
        throw error;
      }
      
      // Atualizar o cache (limpar para forçar recarga dos dados)
      clearPromptsCache();
      
      return true;
    } catch (error) {
      throw error;
    }
  },

  // Sincronizar prompts do localStorage para o Supabase
  syncLocalPromptsToSupabase: async (): Promise<boolean> => {
    try {
      // Verificar se existe a chave antiga de prompts no localStorage
      const oldPromptsKey = 'openai_prompts';
      const oldPromptsData = localStorage.getItem(oldPromptsKey);
      
      if (!oldPromptsData) {
        return false;
      }
      
      // Converter de JSON para objeto
      const oldPrompts = JSON.parse(oldPromptsData);
      
      // Iterar sobre os prompts antigos e salvá-los no Supabase
      for (const key in oldPrompts) {
        if (oldPrompts.hasOwnProperty(key)) {
          const oldPrompt = oldPrompts[key];
          
          await promptsService.createPrompt({
            name: key,
            promptText: oldPrompt,
            description: `Migrado do localStorage (${key})`,
            category: 'Migrado',
            isDefault: false
          });
        }
      }
      
      // Remover a chave antiga do localStorage
      localStorage.removeItem(oldPromptsKey);
      
      return true;
    } catch (error) {
      return false;
    }
  },
  
  // Limpar o cache
  clearCache: clearPromptsCache
}; 